import React, { useState, useEffect, useRef } from 'react';
import { DeployLogConsole } from './DeployLogConsole';
import { MainnetConfirmDialog } from './MainnetConfirmDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileCode, Upload, CheckCircle, XCircle, ExternalLink, 
  Loader2, RefreshCw, Shield, Link2, AlertTriangle, Mail, BadgeCheck,
  History, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminContractDeployPanel - Admin panel for deploying and managing on-chain contracts
 * Features: Auto-deploy, Etherscan verification, email notifications
 */
export const AdminContractDeployPanel = ({ token }) => {
  const [contracts, setContracts] = useState({
    creditAgreement: { mainnet: null, sepolia: null },
    treasuryCheck: { mainnet: null, sepolia: null }
  });
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [mainnetTarget, setMainnetTarget] = useState(null);
  const pollRef = useRef(null);
  const deployingRef = useRef(false);
  const [deployOptions, setDeployOptions] = useState({
    auto_verify: true,
    send_notification: true
  });

  const loadContracts = async () => {
    setLoading(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${API}/pas-admin/contracts/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pas-admin/contracts/deployments`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { deployments: [] } }))
      ]);
      setContracts(statusRes.data);
      const deps = historyRes.data.deployments || [];
      setDeployments(deps);
      const running = deps.find(d => d.status === 'running');
      if (running && !deployingRef.current) {
        deployingRef.current = true;
        setDeploying(`${running.contract_type}-${running.network}`);
        pollJob(running.job_id);
      }
    } catch (e) {
      console.error('Failed to load contracts:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadContracts(); }, [token]);

  const pollJob = async (jobId) => {
    clearTimeout(pollRef.current);
    try {
      const r = await axios.get(`${API}/pas-admin/contracts/deployments/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
      setActiveJob(r.data);
      if (r.data.status === 'running') { pollRef.current = setTimeout(() => pollJob(jobId), 2000); return; }
      pollRef.current = null;
      if (deployingRef.current) {
        deployingRef.current = false;
        setDeploying(null);
        if (r.data.status === 'deployed') toast.success(`Deployed at ${r.data.contract_address}`);
        else toast.error(r.data.error || 'Deployment failed');
        loadContracts();
      }
    } catch (e) {
      pollRef.current = setTimeout(() => pollJob(jobId), 4000);
    }
  };

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const startDeploy = async (contractType, network, confirmText) => {
    setDeploying(`${contractType}-${network}`);
    deployingRef.current = true;
    try {
      const r = await axios.post(`${API}/pas-admin/contracts/deploy`,
        { contract_type: contractType, network, confirm_text: confirmText, ...deployOptions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveJob({ job_id: r.data.job_id, contract_type: contractType, network, status: 'running', logs: [] });
      pollJob(r.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deployment failed');
      deployingRef.current = false;
      setDeploying(null);
    }
  };

  const deployContract = (contractType, network) => {
    const net = networks.find(n => n.id === network);
    if (net?.real) { setMainnetTarget({ contractType, network: net }); return; }
    if (!window.confirm(`Deploy ${contractType} to Sepolia testnet?`)) return;
    startDeploy(contractType, network);
  };

  const verifyContract = async (jobId) => {
    try {
      const r = await axios.post(`${API}/pas-admin/contracts/${jobId}/verify`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.success) {
        toast.success('Contract verified on Etherscan!');
      } else {
        toast.error(r.data.error || 'Verification failed');
      }
      loadContracts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    }
  };

  const networks = contracts.networks || [{ id: 'mainnet', label: 'Ethereum', real: true, explorer: 'https://etherscan.io' }, { id: 'sepolia', label: 'Sepolia (test)', real: false, explorer: 'https://sepolia.etherscan.io' }];

  const NetworkCard = ({ type, net, info }) => (
    <div data-testid={`contract-${type}-${net.id}-card`} className={`rounded-lg p-3 ${info?.deployed ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-700/50'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-medium ${net.real ? 'text-amber-300' : 'text-slate-300'}`}>{net.label}</span>
        {info?.deployed ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-slate-500" />}
      </div>
      {info?.deployed ? (
        <div className="space-y-1">
          <code className="text-xs text-purple-300 break-all block" data-testid={`contract-${type}-${net.id}-address`}>{info.address}</code>
          <a href={`${net.explorer}/address/${info.address}`} target="_blank" rel="noreferrer" className="text-xs text-blue-400 flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Explorer
          </a>
        </div>
      ) : (
        <button onClick={() => deployContract(type, net.id)} disabled={!!deploying} data-testid={`deploy-${type}-${net.id}-btn`}
          className={`w-full mt-2 px-3 py-2 ${net.real ? 'bg-amber-500 hover:bg-amber-600' : 'bg-purple-500 hover:bg-purple-600'} disabled:opacity-50 text-white rounded text-sm flex items-center justify-center gap-2`}>
          {deploying === `${type}-${net.id}` ? <><Loader2 className="w-4 h-4 animate-spin" /> Deploying...</> : <><Upload className="w-4 h-4" /> Deploy to {net.label}</>}
        </button>
      )}
    </div>
  );

  const ContractRow = ({ name, icon: Icon, type, data }) => (
    <div className="bg-slate-900/60 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-purple-500/20 rounded-lg"><Icon className="w-5 h-5 text-purple-400" /></div>
        <h4 className="text-white font-semibold">{name}</h4>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {networks.map(n => <NetworkCard key={n.id} type={type} net={n} info={data?.[n.id]} />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Main Deploy Panel */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl" data-testid="admin-contract-deploy">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileCode className="w-5 h-5 text-purple-400" />
            <h3 className="text-white font-semibold">Smart Contract Deployment</h3>
          </div>
          <button 
            data-testid="contracts-refresh-btn"
            onClick={loadContracts}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-white rounded"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Warning */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              <p className="font-medium">Production Deployment</p>
              <p className="text-amber-300 mt-1">
                Deploying to Ethereum, Base or Arbitrum uses real ETH on that network. Test on Sepolia first. 
                Deployments are permanent and cannot be undone.
              </p>
            </div>
          </div>

          {/* Deploy Options */}
          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-sm text-slate-300 font-medium mb-3">Deployment Options</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="deploy-auto-verify-checkbox"
                  checked={deployOptions.auto_verify}
                  onChange={(e) => setDeployOptions(prev => ({ ...prev, auto_verify: e.target.checked }))}
                  className="rounded border-slate-600"
                />
                <BadgeCheck className="w-4 h-4 text-emerald-400" />
                Auto-verify on Etherscan
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="deploy-notify-checkbox"
                  checked={deployOptions.send_notification}
                  onChange={(e) => setDeployOptions(prev => ({ ...prev, send_notification: e.target.checked }))}
                  className="rounded border-slate-600"
                />
                <Mail className="w-4 h-4 text-blue-400" />
                Email notification
              </label>
            </div>
          </div>

          {contracts.deployer_address && (
            <p className="text-xs text-slate-400" data-testid="deployer-address">
              Deployer wallet: <code className="text-purple-300">{contracts.deployer_address}</code>
            </p>
          )}

          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <ContractRow 
                name="Credit Agreement Registry" 
                icon={Shield}
                type="credit_agreement"
                data={contracts.creditAgreement}
              />
              <ContractRow 
                name="Treasury Check (BTCK)" 
                icon={Link2}
                type="treasury_check"
                data={contracts.treasuryCheck}
              />
            </div>
          )}
        </div>
      </div>

      <DeployLogConsole job={activeJob} onClose={() => setActiveJob(null)} />

      <MainnetConfirmDialog
        open={!!mainnetTarget}
        contractType={mainnetTarget?.contractType}
        network={mainnetTarget?.network}
        confirmText={mainnetTarget?.network?.confirm_text || 'DEPLOY TO MAINNET'}
        deployer={contracts.deployer_address}
        token={token}
        onCancel={() => setMainnetTarget(null)}
        onConfirm={(text) => { const t = mainnetTarget; setMainnetTarget(null); startDeploy(t.contractType, t.network.id, text); }}
      />

      {/* Deployment History */}
      {deployments.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-3">
            <History className="w-5 h-5 text-slate-400" />
            <h3 className="text-white font-semibold">Deployment History</h3>
          </div>
          <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
            {deployments.map(dep => (
              <div key={dep.job_id} data-testid={`deployment-row-${dep.job_id}`} onClick={() => !deployingRef.current && pollJob(dep.job_id)} className={`cursor-pointer rounded-lg p-3 ${
                dep.status === 'deployed' ? 'bg-emerald-500/10' :
                dep.status === 'failed' ? 'bg-red-500/10' :
                'bg-slate-700/50'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">
                      {dep.contract_type} → {dep.network}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(dep.started_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {dep.status === 'deployed' && (
                      <>
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        {dep.verification?.success ? (
                          <BadgeCheck className="w-4 h-4 text-blue-400" title="Verified" />
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); verifyContract(dep.job_id); }}
                            data-testid={`verify-${dep.job_id}-btn`}
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            Verify
                          </button>
                        )}
                        <a href={dep.explorer_url} target="_blank" rel="noreferrer">
                          <ExternalLink className="w-4 h-4 text-slate-400 hover:text-white" />
                        </a>
                      </>
                    )}
                    {dep.status === 'failed' && (
                      <span className="text-xs text-red-400">{dep.error?.slice(0, 50)}</span>
                    )}
                    {dep.status === 'running' && (
                      <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                    )}
                  </div>
                </div>
                {dep.contract_address && (
                  <code className="text-xs text-purple-300 mt-1 block">{dep.contract_address}</code>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContractDeployPanel;
